import { useEffect, useState } from 'react';
import {
  INVOICE_PAYMENT_SYNC_STATUS_LABELS,
  QBO_PAYMENT_SYNC_DEFAULT_INTERVAL_MINUTES,
  QBO_PAYMENT_SYNC_MAX_INTERVAL_MINUTES,
  QBO_PAYMENT_SYNC_MIN_INTERVAL_MINUTES,
  type InvoicePaymentSyncStatus,
  type QboPaymentSyncResult,
} from '@catalyst-psa/shared';

import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { CheckboxField, TextField } from '../../components/ui/Field.js';
import { formatDateTime } from '../../utils/date.js';
import { mutationError } from '../../api/client.js';
import {
  useQboPaymentSyncSchedule,
  useRunQboPaymentSync,
  useUpdateQboPaymentSyncSchedule,
} from './api.js';

type BadgeTone = NonNullable<Parameters<typeof Badge>[0]['tone']>;

const OUTCOME_TONES: Record<string, BadgeTone> = {
  ok: 'green',
  partial: 'amber',
  failed: 'red',
};

const STATUS_TONES: Record<InvoicePaymentSyncStatus, BadgeTone> = {
  paid: 'green',
  partial: 'amber',
  open: 'blue',
  overdue: 'red',
  not_found: 'gray',
};

/**
 * Scheduled invoice payment sync card on the QuickBooks integration page: scans QBO
 * for paid/overdue invoices, mirrors the state onto PSA invoices, and backfills
 * missing QBO deep links. Read-only against QuickBooks.
 */
export function PaymentSyncCard(): JSX.Element {
  const scheduleQuery = useQboPaymentSyncSchedule();
  const updateSchedule = useUpdateQboPaymentSyncSchedule();
  const runNow = useRunQboPaymentSync();

  const [enabled, setEnabled] = useState(false);
  const [intervalMinutes, setIntervalMinutes] = useState(
    String(QBO_PAYMENT_SYNC_DEFAULT_INTERVAL_MINUTES),
  );
  const [runResult, setRunResult] = useState<QboPaymentSyncResult | null>(null);

  const schedule = scheduleQuery.data;

  useEffect(() => {
    if (!schedule) return;
    setEnabled(schedule.enabled);
    setIntervalMinutes(
      String(schedule.intervalMinutes ?? QBO_PAYMENT_SYNC_DEFAULT_INTERVAL_MINUTES),
    );
  }, [schedule]);

  function handleSave(): void {
    const parsed = Number(intervalMinutes);
    updateSchedule.mutate({
      enabled,
      intervalMinutes: Number.isFinite(parsed) && parsed > 0 ? Math.trunc(parsed) : null,
    });
  }

  function handleRunNow(): void {
    setRunResult(null);
    runNow.mutate(undefined, { onSuccess: (result) => setRunResult(result) });
  }

  const errorText =
    mutationError(updateSchedule.error, 'Could not save the schedule.') ??
    mutationError(runNow.error, 'The payment sync failed to run.');

  return (
    <div className="mb-6 rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-1 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-slate-700">Invoice payment sync</h2>
        {schedule?.lastOutcome ? (
          <Badge tone={OUTCOME_TONES[schedule.lastOutcome] ?? 'gray'}>
            {`Last run ${schedule.lastOutcome}`}
          </Badge>
        ) : null}
      </div>
      <p className="mb-4 text-sm text-slate-600">
        Scans QuickBooks on a schedule and mirrors the payment state onto matching PSA
        invoices: paid invoices are marked <span className="font-medium">Paid</span>,
        past-due invoices are marked <span className="font-medium">Overdue</span>, unmatched
        invoices are marked <span className="font-medium">Not found in QBO</span>, and a
        missing QuickBooks link is filled in. Invoices match by their stored QuickBooks id,
        then by number (e.g. INV-012143, falling back to 12143). Fully paid + linked
        invoices are skipped.
      </p>

      {scheduleQuery.isLoading ? (
        <p className="text-sm text-slate-500">Loading schedule…</p>
      ) : scheduleQuery.isError ? (
        <p className="text-sm text-rose-600">Could not load the payment-sync schedule.</p>
      ) : schedule ? (
        <>
          <div className="flex flex-wrap items-end gap-4">
            <CheckboxField
              label="Run on a schedule"
              checked={enabled}
              onChange={(event) => setEnabled(event.target.checked)}
            />
            <div className="w-44">
              <TextField
                label="Interval (minutes)"
                type="number"
                min={QBO_PAYMENT_SYNC_MIN_INTERVAL_MINUTES}
                max={QBO_PAYMENT_SYNC_MAX_INTERVAL_MINUTES}
                step={60}
                value={intervalMinutes}
                onChange={(event) => setIntervalMinutes(event.target.value)}
              />
            </div>
            <Button
              variant="secondary"
              onClick={handleSave}
              isLoading={updateSchedule.isPending}
            >
              Save schedule
            </Button>
            <Button onClick={handleRunNow} isLoading={runNow.isPending}>
              Run now
            </Button>
          </div>

          <dl className="mt-4 grid grid-cols-2 gap-4 text-sm sm:grid-cols-4">
            <SummaryItem label="Last run" value={formatDateTime(schedule.lastRunAt)} />
            <SummaryItem label="Next run" value={formatDateTime(schedule.nextRunAt)} />
            <SummaryItem
              label="Scanned / matched"
              value={`${schedule.lastScanned} / ${schedule.lastMatched}`}
            />
            <SummaryItem
              label="Paid / overdue / not found / links"
              value={`${schedule.lastMarkedPaid} / ${schedule.lastMarkedOverdue} / ${schedule.lastMarkedNotFound} / ${schedule.lastLinksBackfilled}`}
            />
          </dl>
          {schedule.lastMessage ? (
            <p className="mt-2 text-sm text-amber-700">{schedule.lastMessage}</p>
          ) : null}
        </>
      ) : null}

      {errorText ? <p className="mt-3 text-sm text-rose-600">{errorText}</p> : null}

      {runResult ? (
        <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-4">
          <p className="text-sm text-slate-700">
            <span className="font-medium">Run complete:</span> scanned {runResult.scanned},
            matched {runResult.matched} ({runResult.unmatched} unmatched) — {runResult.markedPaid}{' '}
            marked paid, {runResult.markedOverdue} marked overdue, {runResult.markedNotFound}{' '}
            marked not found, {runResult.linksBackfilled}{' '}
            link{runResult.linksBackfilled === 1 ? '' : 's'} backfilled.
            {runResult.message ? ` ${runResult.message}` : ''}
          </p>
          {runResult.changes.length > 0 ? (
            <div className="mt-3 overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead className="text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-3 py-2 font-medium">Invoice</th>
                    <th className="px-3 py-2 font-medium">Customer</th>
                    <th className="px-3 py-2 font-medium">Payment status</th>
                    <th className="px-3 py-2 font-medium">QBO match</th>
                    <th className="px-3 py-2 font-medium">Link</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {runResult.changes.map((change) => (
                    <tr key={change.invoiceId}>
                      <td className="px-3 py-2 font-medium text-slate-900">{change.number}</td>
                      <td className="px-3 py-2 text-slate-600">{change.customerName ?? '—'}</td>
                      <td className="px-3 py-2">
                        <span className="inline-flex items-center gap-1.5">
                          <span className="text-slate-500">
                            {change.previousStatus
                              ? INVOICE_PAYMENT_SYNC_STATUS_LABELS[change.previousStatus]
                              : 'Unset'}
                            {' → '}
                          </span>
                          <Badge tone={STATUS_TONES[change.newStatus]}>
                            {INVOICE_PAYMENT_SYNC_STATUS_LABELS[change.newStatus]}
                          </Badge>
                        </span>
                      </td>
                      <td className="px-3 py-2 text-slate-600">
                        {change.qboDocNumber ?? 'Stored id'}
                      </td>
                      <td className="px-3 py-2 text-slate-600">
                        {change.linkBackfilled ? 'Backfilled' : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="mt-2 text-sm text-slate-500">No invoices changed this run.</p>
          )}
        </div>
      ) : null}
    </div>
  );
}

function SummaryItem({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="mt-1 font-medium text-slate-900">{value}</dd>
    </div>
  );
}
